import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Bot, Send, User, Sparkles, PhoneOff, Radio, ShieldOff } from "lucide-react";

function SmsDeliveryPill({ status, errorCode }) {
  if (errorCode) {
    return <Badge className="bg-rose-500/15 text-rose-300 border-rose-500/40 text-[10px] uppercase tracking-wider" data-testid="sms-failed-pill">SMS Failed · {errorCode}</Badge>;
  }
  if (status === "delivered") return <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/40 text-[10px] uppercase tracking-wider" data-testid="sms-delivered-pill">Delivered</Badge>;
  if (status === "sent") return <Badge className="bg-sky-500/15 text-sky-300 border-sky-500/40 text-[10px] uppercase tracking-wider" data-testid="sms-sent-pill">Sent</Badge>;
  if (status === "undelivered" || status === "failed") return <Badge className="bg-rose-500/15 text-rose-300 border-rose-500/40 text-[10px] uppercase tracking-wider" data-testid="sms-undelivered-pill">Undelivered</Badge>;
  if (status === "demo_not_sent") return <Badge className="bg-violet-500/15 text-violet-300 border-violet-500/40 text-[10px] uppercase tracking-wider" data-testid="sms-demo-pill">Demo · not sent</Badge>;
  if (status === "not_sent") return <Badge className="bg-slate-700 text-slate-400 border-slate-600 text-[10px] uppercase tracking-wider" data-testid="sms-not-sent-pill">Not sent</Badge>;
  return <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/40 text-[10px] uppercase tracking-wider" data-testid="sms-pending-pill">Pending</Badge>;
}

export default function ChatView() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [messages, setMessages] = useState([]);
  const [current, setCurrent] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [twilio, setTwilio] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get("/leads").then((r) => {
      setLeads(r.data);
      if (!leadId && r.data.length) navigate(`/app/chat/${r.data[0].id}`, { replace: true });
    });
    api.get("/twilio/status").then((r) => setTwilio(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!leadId) return;
    const lead = leads.find(l => l.id === leadId);
    setCurrent(lead || null);
    api.get(`/leads/${leadId}/messages`).then(r => setMessages(r.data));
  }, [leadId, leads]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const simulateHomeowner = async () => {
    if (!text.trim() || !leadId) return;
    setSending(true);
    const t = text;
    setText("");
    try {
      const { data } = await api.post(`/leads/${leadId}/ai-reply`, { lead_id: leadId, text: t });
      setMessages((m) => [...m, data.homeowner_message, data.ai_message]);
      if (data.sms?.sent) {
        toast.success(`AI replied — SMS delivered (${data.sms.status})`);
      } else if (data.sms?.error === "twilio_not_configured") {
        toast.success("AI replied (simulation — Twilio not configured)");
      } else {
        toast.warning(`AI replied but SMS failed: ${data.sms?.error || "unknown"}`);
      }
    } catch (e) {
      toast.error("AI could not reply — check backend logs");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="h-screen flex">
      {/* Lead list */}
      <div className="w-80 border-r border-slate-800 bg-slate-900/40 flex flex-col shrink-0">
        <div className="p-4 border-b border-slate-800">
          <p className="font-mono text-xs uppercase tracking-widest text-orange-400 mb-1">Conversations</p>
          <h2 className="font-display text-xl font-bold">AI Chat</h2>
        </div>
        <div className="flex-1 overflow-y-auto">
          {leads.map((l) => (
            <button key={l.id} onClick={() => navigate(`/app/chat/${l.id}`)}
              data-testid={`chat-lead-${l.id}`}
              className={`w-full text-left px-4 py-3 border-b border-slate-800/50 transition-colors ${leadId === l.id ? "bg-orange-500/10 border-l-2 border-l-orange-500" : "hover:bg-slate-800/60"}`}>
              <div className="flex justify-between items-start gap-2 mb-1">
                <p className="font-semibold text-sm truncate">{l.caller_name}</p>
                <span className="font-mono text-xs text-emerald-400">${(l.estimated_value/1000).toFixed(1)}k</span>
              </div>
              <p className="text-xs text-slate-400 truncate">{l.issue}</p>
              <p className="text-xs text-slate-500 font-mono mt-1">{l.caller_phone}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Chat pane */}
      <div className="flex-1 flex flex-col min-w-0">
        {current ? (
          <>
            <div className="h-16 border-b border-slate-800 px-6 flex items-center justify-between shrink-0 bg-slate-900/40">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center">
                  <PhoneOff className="w-4 h-4 text-orange-400" />
                </div>
                <div>
                  <p className="font-semibold">{current.caller_name}</p>
                  <p className="text-xs text-slate-500 font-mono">{current.caller_phone} · {current.address}</p>
                </div>
              </div>
              <div className="flex gap-2 items-center">
                <Badge className="bg-orange-500/15 text-orange-300 border border-orange-500/30">{current.issue}</Badge>
                <Badge className="bg-slate-800 text-slate-300 border border-slate-700 capitalize">{current.status}</Badge>
                {twilio && (twilio.configured && twilio.company_number_assigned ? (
                  <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30" data-testid="twilio-live-badge">
                    <Radio className="w-3 h-3 mr-1" /> Live SMS
                  </Badge>
                ) : (
                  <Badge className="bg-amber-500/15 text-amber-300 border border-amber-500/30" data-testid="twilio-sim-badge">
                    <ShieldOff className="w-3 h-3 mr-1" /> Simulation
                  </Badge>
                ))}
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-4">
              {messages.length === 0 && (
                <div className="text-center py-16">
                  <Sparkles className="w-8 h-8 text-orange-400 mx-auto mb-3" />
                  <p className="text-slate-400 text-sm max-w-sm mx-auto">
                    No conversation yet. When this homeowner texts back, their messages and the AI replies will appear here.
                  </p>
                  <p className="text-slate-500 text-xs mt-2">
                    You can preview what the AI would reply using the <span className="text-amber-300">Test tool</span> below.
                  </p>
                </div>
              )}
              {messages.map((m) => {
                const isAI = m.sender === "ai";
                const isHome = m.sender === "homeowner";
                return (
                  <div key={m.id} className={`flex gap-3 ${isHome ? "justify-end" : ""}`} data-testid={`msg-${m.id}`}>
                    {!isHome && (
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${isAI ? "bg-orange-500/20" : "bg-slate-800"}`}>
                        {isAI ? <Bot className="w-4 h-4 text-orange-400" /> : <User className="w-4 h-4 text-slate-300" />}
                      </div>
                    )}
                    <div className={`max-w-xl px-4 py-2.5 rounded-2xl border ${isHome ? "bg-slate-800 border-slate-700 rounded-tr-sm" : isAI ? "bg-orange-500/10 border-orange-500/30 rounded-tl-sm" : "bg-slate-800 border-slate-700"}`}>
                      <p className="text-sm text-slate-100 leading-relaxed">{m.text}</p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <p className="text-xs text-slate-500 font-mono">{isAI ? "AI" : isHome ? "Homeowner" : "Agent"} · {new Date(m.created_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</p>
                        {isAI && (m.twilio_sid || m.sms_status === "demo_not_sent") && (
                          <SmsDeliveryPill status={m.sms_status} errorCode={m.sms_error_code} />
                        )}
                        {isAI && !m.twilio_sid && m.sms_error === "twilio_not_configured" && (
                          <Badge className="bg-slate-700 text-slate-400 border-slate-600 text-[10px] uppercase tracking-wider" data-testid="sim-pill">Simulation</Badge>
                        )}
                      </div>
                    </div>
                    {isHome && (
                      <div className="w-8 h-8 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center shrink-0">
                        <User className="w-4 h-4 text-slate-300" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="border-t border-slate-800 p-4 bg-slate-900/40 shrink-0">
              <div className="flex items-center gap-2 mb-2">
                <Badge className="bg-amber-500/15 text-amber-300 border border-amber-500/40 text-[10px] uppercase tracking-wider" data-testid="dev-tool-badge">
                  Test tool
                </Badge>
                <p className="text-xs text-slate-400">
                  Preview the AI reply — type what the homeowner might text. The AI drafts a response and <span className="text-slate-300">only sends real SMS if Twilio is live and the homeowner opted in</span>.
                </p>
              </div>
              <div className="flex gap-2 border border-dashed border-amber-500/30 rounded-lg p-2 bg-amber-500/5">
                <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && simulateHomeowner()}
                  placeholder="Type as the homeowner would…" className="bg-slate-800 border-slate-700 text-white" data-testid="chat-input" disabled={sending} />
                <Button onClick={simulateHomeowner} disabled={sending || !text.trim()} className="bg-orange-500 hover:bg-orange-600 text-slate-900 font-bold" data-testid="chat-send">
                  {sending ? "AI thinking…" : <><Send className="w-4 h-4 mr-1" /> Test AI</>}
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-slate-500">Select a conversation.</div>
        )}
      </div>
    </div>
  );
}
